<template>
  <div>
    <div v-if="project" class="shell-container">
      <header class="router-header flex flex-wrap content-center items-center transition ease-in-out container px-8">
        <div class="flex-1">
          <button class="bg-gradient text-white font-bold p-1 xl:p-2 rounded-full" @click="routerBack()">
            <svg xmlns="http://www.w3.org/2000/svg" class="h-5 w-5 xl:h-6 xl:w-6" viewBox="0 0 20 20" fill="currentColor">
              <path fill-rule="evenodd" d="M9.707 14.707a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414l4-4a1 1 0 011.414 1.414L7.414 9H15a1 1 0 110 2H7.414l2.293 2.293a1 1 0 010 1.414z" clip-rule="evenodd" />
            </svg>
          </button>
        </div>
        <ul class="breadcrumbs bg-transparent items-center text-center self-auto">
          <li @click="$router.push({ name: 'projects' })">
            <span class="block uppercase tracking-wide text-xxs xl:text-xs font-semibold opacity-75">Project</span>
            <span class="block tracking-wide text-xxs xl:text-xs font-bold">{{ project.name }}</span>
            <div class="p-0.5 mt-1" />
          </li>
          <li class="divider">
            <svg viewBox="0 0 20 20" fill="currentColor" class="w-6 h-6 text-gray-600">
              <path fill-rule="evenodd" d="M7.293 14.707a1 1 0 010-1.414L10.586 10 7.293 6.707a1 1 0 011.414-1.414l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0z" clip-rule="evenodd" />
            </svg>
          </li>
          <li>
            <span class="block uppercase tracking-wide text-xxs xl:text-xs font-semibold opacity-75">Floor</span>
            <span class="block tracking-wide  text-xxs xl:text-xs font-bold">{{ currentFloorName }}</span>
            <div class="bg-gradient-green p-0.5 mt-1 rounded-lg" />
          </li>
        </ul>

        <ul class="flex flex-1 text-right flex-col fabs">
          <li>
            <span class="font-semibold xl:font-bold text-xs xl:text-sm">Change Floor</span>
            <button class="bg-gradient text-white font-bold p-1 xl:p-2 rounded-full hover:opacity-50" @click="showFloors = true">
              <svg xmlns="http://www.w3.org/2000/svg" class="h-5 w-5 xl:h-6 xl:w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 6h16M4 12h16M4 18h16" />
              </svg>
            </button>
          </li>
          <li>
            <span class="font-semibold xl:font-bold text-xs xl:text-sm">New Room</span>
            <button class="bg-gradient text-white font-bold p-1 xl:p-2 rounded-full hover:opacity-50" @click="newRoomModalShow = true">
              <svg xmlns="http://www.w3.org/2000/svg" class="h-5 w-5 xl:h-6 xl:w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
              </svg>
            </button>
          </li>
        </ul>
      </header>

      <swiper
        v-if="project.floors[selectedFloorIndex !== null ? selectedFloorIndex : currentFloor] && project.floors[selectedFloorIndex !== null ? selectedFloorIndex : currentFloor].rooms.length > 0"
        ref="roomsSwiper"
        class="swiper list-selector featured-image f-5"
        :options="swiperOptions"
        :auto-update="true"
        :auto-destroy="false"
        :delete-instance-on-destroy="false"
        :cleanup-styles-on-destroy="true"
      >
        <swiper-slide v-for="room in project.floors[selectedFloorIndex !== null ? selectedFloorIndex : currentFloor].rooms" :key="room.index">
          <router-link :to="{ name: 'projectRoomProducts', params: { projectId: project.id, roomId: room.id}}" class="absolute w-full h-full">
            <div v-if="room.image_url" class="slide--image">
              <img :src="room.image_url" alt="Specifi">
            </div>
            <div class="slide--padded p-3 pt-1 text-center" :style="!room.image_url && 'margin-top: 25%;'">
              <h3 class="text-sm font-medium">
                {{ room.name }}
              </h3>
              <span class="text-xs">{{ 'Product' | pluralize(totalProductsCount(room.room_products.length, room.room_custom_products.length, room.room_placeholder_products), true) }}</span> 
              <div :class="showPricing ? 'opacity-100' : 'opacity-0'" class="transition-all ease-in-out duration-200">
                <span v-if="room.room_cost" class="text-xxs xl:text-xs">{{ getRoomCost(room) | currency }}</span>
              </div>
            </div>
          </router-link>
          <div class="absolute bottom-5 w-full px-4 flex items-center justify-between">
            <products-preview :products="room.room_products" :total-products="totalProductsCount(room.room_products.length, room.room_custom_products.length, room.room_placeholder_products)" :show-no-products="true" />
            <button class="bg-lime text-white font-bold py-3 px-3 w-8 h-8 xl:w-10 xl:h-10 rounded-full" @click="editRoom(room)">
              <img src="@/assets/img/icons/edit.svg" alt="Edit">
            </button>
          </div>
        </swiper-slide>

        <div slot="button-prev" :class="$isTouchDevice && 'hide'" class="swiper-btn swiper-btn-prev">
          <svg fill="none" viewBox="0 0 24 24" stroke="currentColor" class="w-12 h-12">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 19l-7-7 7-7" />
          </svg>
        </div>
        <div slot="button-next" :class="$isTouchDevice && 'hide'" class="swiper-btn swiper-btn-next">
          <svg fill="none" viewBox="0 0 24 24" stroke="currentColor" class="w-12 h-12">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5l7 7-7 7" />
          </svg>
        </div>
      </swiper>
      <div v-else class="flex items-center justify-center flex-col text-gray-900 py-8 border-dashed border-gray-300 border rounded-md my-2">
        <svg viewBox="0 0 512 512" class="w-24 h-24 mb-2" fill="currentColor" stroke="currentColor" xmlns="http://www.w3.org/2000/svg">
          <path d="m310.908 405.799v-32.089c0-3.657-1.503-7.002-3.94-9.411-2.409-2.438-5.754-3.94-9.411-3.94-82.659 0-165.319 0-247.978 0-3.657 0-7.002 1.503-9.411 3.94-2.438 2.409-3.94 5.754-3.94 9.411v32.089zm110.836-172.405h53.773v-15.761h-53.773zm53.774 14.995h-53.773v14.117h53.773zm0 29.112h-53.773v15.761h53.773zm-286.53-235.249c7.2 0 13.691 2.892 18.425 7.597 4.706 4.733 7.626 11.225 7.626 18.425 0 7.172-2.92 13.691-7.626 18.397-4.733 4.705-11.225 7.625-18.425 7.625-7.172 0-13.663-2.92-18.397-7.625-4.706-4.706-7.625-11.226-7.625-18.397 0-7.2 2.92-13.691 7.625-18.425 4.734-4.705 11.225-7.597 18.397-7.597zm7.796 18.226c-1.984-2.013-4.734-3.231-7.795-3.231-3.033 0-5.783 1.219-7.767 3.231-2.013 1.984-3.232 4.733-3.232 7.795 0 3.033 1.219 5.783 3.232 7.796 1.984 1.984 4.734 3.203 7.767 3.203 3.062 0 5.811-1.219 7.795-3.203 2.013-2.013 3.231-4.763 3.231-7.796 0-3.061-1.219-5.81-3.231-7.795zm-28.205 97.967 12.302-14.429-24.945-29.255-33.336 39.063v4.621zm22.138-25.966 60.237-70.612c2.268-2.636 5.074-4.394 8.617-4.479 3.827-.057 6.86 1.843 9.185 4.79l42.351 49.635 32.258-37.815c2.382-2.664 5.159-4.479 8.845-4.449 3.571.027 6.435 1.757 8.702 4.449l28.488 33.422v-77.018h-116.421c-4.139 0-7.483-3.346-7.483-7.484 0-4.139 3.345-7.512 7.483-7.512h120.134c3.119 0 5.953 1.275 7.994 3.316s3.316 4.876 3.316 7.994v135.441c0 3.118-1.275 5.924-3.316 7.994-2.041 2.041-4.875 3.316-7.994 3.316-91.418 0-182.809 0-274.198 0-3.118 0-5.953-1.275-7.994-3.316-2.041-2.07-3.316-4.876-3.316-7.994v-135.44c0-3.118 1.275-5.953 3.316-7.994s4.875-3.316 7.994-3.316h120.105c4.139 0 7.512 3.373 7.512 7.512s-3.373 7.484-7.512 7.484h-116.42v100.376l24.576-28.801c2.183-2.551 5.33-4.195 8.703-4.195s6.662 1.673 8.816 4.195zm130.197-9.128 29.963 35.095h38.523v-27.979l-37.276-43.711zm10.29 35.094c-23.811-27.922-47.651-55.872-71.491-83.794-23.811 27.922-47.65 55.872-71.462 83.794zm83.056 44.192h68.771c4.138 0 7.482 3.345 7.482 7.483v90.654c0 4.139-3.345 7.512-7.482 7.512h-26.902v37.077h48.389c4.139 0 7.483 3.373 7.483 7.512v136.235c0 4.138-3.345 7.483-7.483 7.483h-111.745c-4.139 0-7.512-3.346-7.512-7.483v-27.611c0-4.138 3.373-7.482 7.512-7.482s7.483 3.345 7.483 7.482v20.099h96.748v-71.915h-96.748v17.857c0 4.139-3.345 7.512-7.483 7.512-4.139 0-7.512-3.373-7.512-7.512v-74.665c0-4.139 3.373-7.512 7.512-7.512h48.359v-37.077h-26.872c-4.139 0-7.512-3.373-7.512-7.512 0-30.219 0-60.436 0-90.654 0-4.139 3.373-7.483 7.512-7.483zm51.251 233.407c0-4.138 3.345-7.512 7.484-7.512s7.512 3.374 7.512 7.512v19.191c0 4.139-3.373 7.512-7.512 7.512-4.14 0-7.484-3.373-7.484-7.512zm-65.254-41.358h96.748v-34.328h-96.748zm-400.257 18.624c0-4.139 3.374-7.512 7.512-7.512h13.72v-32.088c0-7.796 3.203-14.882 8.333-20.013 3.43-3.43 7.711-5.981 12.501-7.313v-59.16c0-7.795 3.203-14.882 8.334-20.013s12.217-8.334 20.013-8.334h86.174c4.139 0 7.483 3.374 7.483 7.512 0 4.139-3.345 7.513-7.483 7.513h-86.173c-3.657 0-7.002 1.502-9.412 3.911s-3.94 5.755-3.94 9.411v58.14h16.272v-20.296c0-6.321 2.58-12.048 6.747-16.214 4.139-4.139 9.893-6.719 16.186-6.719h33.053c6.321 0 12.047 2.58 16.214 6.719 4.139 4.166 6.718 9.893 6.718 16.214v20.296h42.633v-20.296c0-6.321 2.58-12.048 6.718-16.214 4.167-4.139 9.893-6.719 16.215-6.719h33.052c6.293 0 12.047 2.58 16.186 6.719 4.167 4.166 6.747 9.893 6.747 16.214v20.296h16.271v-58.14c0-3.656-1.53-7.002-3.939-9.411-2.41-2.409-5.755-3.911-9.411-3.911h-86.175c-4.139 0-7.483-3.374-7.483-7.513 0-4.138 3.345-7.512 7.483-7.512h86.175c7.795 0 14.882 3.203 20.013 8.334s8.334 12.218 8.334 20.013v59.16c4.791 1.332 9.071 3.884 12.501 7.313 5.131 5.131 8.334 12.217 8.334 20.013v32.089h13.72c4.139 0 7.512 3.373 7.512 7.512v75.8c0 4.138-3.373 7.483-7.512 7.483h-332.114c-4.139 0-7.512-3.346-7.512-7.483 0-25.287 0-50.544 0-75.801zm14.996 30.388h141.592c4.139 0 7.483 3.373 7.483 7.512s-3.345 7.483-7.483 7.483h-141.592v22.904h317.144v-22.904h-141.593c-4.139 0-7.483-3.345-7.483-7.483 0-4.139 3.345-7.512 7.483-7.512h141.593v-22.904c-105.705 0-211.439 0-317.144 0v22.904zm243.811-98.335v-20.296c0-2.183-.907-4.167-2.354-5.585-1.445-1.445-3.43-2.353-5.584-2.353h-33.052c-2.183 0-4.167.907-5.585 2.353-1.446 1.418-2.353 3.402-2.353 5.585v20.296zm-121.552 0v-20.296c0-2.183-.907-4.167-2.353-5.585-1.417-1.445-3.401-2.353-5.584-2.353h-33.052c-2.154 0-4.139.907-5.584 2.353-1.446 1.418-2.353 3.402-2.353 5.585v20.296z" />
        </svg>
        <h4 class="text-l">
          There are no rooms on <span class="font-bold">{{ currentFloorName }}</span>
        </h4>
        <span class="font-bold inline-block align-middle text-center text-xs" @click="newRoomModalShow = true">New Room <button class="bg-gradient text-white font-bold py-2 px-2 rounded-full">
          <svg xmlns="http://www.w3.org/2000/svg" class="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
          </svg>
        </button></span>
      </div>
      <footer class="footerActions px-8 absolute">
        <ul class="flex flex-1 text-left flex-col">
          <li @click="goToProjectOverview">
            <button class="bg-primary text-white font-bold p-1 xl:p-2 rounded-full mr-4">
              <svg fill="none" viewBox="0 0 24 24" stroke="currentColor" class="view-grid-add w-5 h-5 xl:w-6 xl:h-6">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M17 14v6m-3-3h6M6 10h2a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v2a2 2 0 002 2zm10 0h2a2 2 0 002-2V6a2 2 0 00-2-2h-2a2 2 0 00-2 2v2a2 2 0 002 2zM6 20h2a2 2 0 002-2v-2a2 2 0 00-2-2H6a2 2 0 00-2 2v2a2 2 0 002 2z" />
              </svg>
            </button>
            <span class="text-xs xl:text-sm font-semibold">Project Overview</span>
          </li>
          <li @click="togglePricing">
            <button class="bg-gradient text-white font-bold p-1 xl:p-2 rounded-full mr-4">
              <svg fill="none" viewBox="0 0 24 24" stroke="currentColor" class="currency-pound w-5 h-5 xl:w-6 xl:h-6">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 9a2 2 0 10-4 0v5a2 2 0 01-2 2h6m-6-4h4m8 0a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            </button>
            <span class="text-xs xl:text-sm font-semibold">{{ this.$store.state.system.showPricing ? 'Hide Pricing' : 'Show Pricing' }}</span>
          </li>
        </ul>
      </footer>
    </div>
    <div v-else-if="loading" class="flex justify-center py-20 my-20">
      <img src="@/assets/img/specifi_logo_blue.svg" width="350" height="175" alt="specifi">
      <svg id="L4" height="100px" version="1.1" xmlns="http://www.w3.org/2000/svg" xmlns:xlink="http://www.w3.org/1999/xlink" x="0px" y="0px" viewBox="0 -25 60 100" enable-background="new 0 0 0 0" xml:space="preserve">
        <circle fill="#77d183" stroke="none" cx="6" cy="50" r="6">
          <animate
            attributeName="opacity"
            dur="1s"
            values="0;1;0"
            repeatCount="indefinite"
            begin="0.1"
          />    
        </circle>
        <circle fill="#85b177" stroke="none" cx="26" cy="50" r="6">
          <animate
            attributeName="opacity"
            dur="1s"
            values="0;1;0"
            repeatCount="indefinite" 
            begin="0.2"
          />       
        </circle>
        <circle fill="#0e2d29" stroke="none" cx="46" cy="50" r="6">
          <animate
            attributeName="opacity"
            dur="1s"
            values="0;1;0"
            repeatCount="indefinite" 
            begin="0.3"
          />     
        </circle>
      </svg>
    </div>

    <new-room-modal 
      v-if="project"
      :show-modal="newRoomModalShow"
      :project-id="project.id"
      :floor="project.floors && project.floors[selectedFloorIndex !== null ? selectedFloorIndex : currentFloor] ? project.floors[selectedFloorIndex !== null ? selectedFloorIndex : currentFloor].id : 0"
      @closeModal="newRoomModalShow = false"
      @addRoom="addRoom"
    />

    <edit-room-modal
      :show-modal="editRoomModalShow"
      :room-data="editRoomData"
      @closeModal="editRoomModalShow = false"
      @updateRoom="updateRoom"
      @removeRoom="removeRoom"
      @success="roomSuccessfullyUpdated"
      @error="roomErrorOnUpdate"
    />

    <floors-modal 
      v-if="project"
      :show-modal="showFloors"
      :floors="project.floors"
      :current-floor="selectedFloorIndex !== null ? selectedFloorIndex : currentFloor"
      :projectrooms="projectrooms"
      @selectFloor="selectFloor"
      @selectFirstAvailableFloor="selectFirstAvailableFloor"
      @closeModal="showFloors = false"
    />
  </div>
</template>

<script>
import NewRoomModal from "../../../components/modals/NewRoomModal.vue";
import EditRoomModal from "../../../components/modals/EditRoomModal.vue";
import FloorsModal from "../../../components/modals/FloorsModal.vue";
import ProductsPreview from "../../../components/ProductsPreview.vue";

export default {
  components: {
    FloorsModal,
     NewRoomModal,
     EditRoomModal,
     ProductsPreview
  },
  data() {
    return {
      newRoomModalShow: false,
      activeSwiper: true,
      showFloors: false,
      projectLoaded: false,
      selectedFloorIndex: null,
      projectrooms: [],
      editRoomModalShow: false,
       editRoomData: {
        img: null,
        is_comms: null,
        name: null,
        distance_from_rack: null,
        ceiling_depth: null,
        ethernet_oints: null,
        phone_oints: null,
        integrated_audio_video: "No",
        scope: null
      },
      showSuccessNotification: false,
      roomNotificationTitle: '',
      roomNotificationMsg: '',
      swiperOptions: {
        navigation: {
          nextEl: '.swiper-btn-next',
          prevEl: '.swiper-btn-prev'
        },
        loop: false,
        loopedSlides: 5,
        spaceBetween: 10,
        centeredSlides: true,
        slidesPerView: "auto",
        touchRatio: 1,
        slideToClickedSlide: true,
        freeMode: true,
        freeModeSticky: true,
        freeModeMomentumRatio: 0.2,
        keyboard: {
          enabled: true
        }
      },
      loading: true
    };
  },
  computed: {
    swiper() {
      return this.$refs.roomsSwiper.$swiper;
    },
    project() {
      return this.$store.state.project.project;
    },
    projectRooms() {
      if (this.projectrooms.length) {
        return this.projectrooms.filter(element => element.floor === this.currentFloor);
      }
      return [];
    },
    currentFloorName() {
      const floorName = this.project.floors[this.selectedFloorIndex !== null ? this.selectedFloorIndex : this.currentFloor] ? this.project.floors[this.selectedFloorIndex !== null ? this.selectedFloorIndex : this.currentFloor].name : '';
      return floorName;
    },
    showPricing() {
      return this.$store.state.system.showPricing;
    },
    currentFloor(){
      if (this.project) {
        if (this.project.floors[0].is_comms !== 1){
          return 0;
        } else if (this.project.floors[1]) {
          if (this.project.floors[1].is_comms !== 1){
              return 1;
          } return 0;
        } else return 0;
      } else return 0;
    }
  },
  updated() {
        this.loading = false;
  },
  methods: {
    addRoom(room) {
      this.$store.dispatch('project/addRoom', { room: room });
    },
    updateRoom(room) {
      this.$store.dispatch('project/updateRoom', { floorId: this.project.floors[this.selectedFloorIndex !== null ? this.selectedFloorIndex : this.currentFloor].id, room: room });
    },
    removeRoom(roomId) {
      this.$store.dispatch('project/removeRoom', { floorId: this.project.floors[this.selectedFloorIndex !== null ? this.selectedFloorIndex : this.currentFloor].id, roomId: roomId });
    },
    routerBack() {
      this.$router.go(-1);
    },
    editRoom(room) {
      this.editRoomData = {...room};
      this.editRoomModalShow = true;
    },
    selectFloor(floor) {
      this.selectedFloorIndex = floor;
    },
    selectFirstAvailableFloor() {
      this.selectFloor(0);
    },
    roomErrorOnUpdate() {
      this.$notify({ 
        type: 'error', 
        title: 'Error', 
        text: 'There was an error updating the room. Please check the form and try again.' 
      });
    },
    roomSuccessfullyUpdated(type) {
      this.editRoomModalShow = false;
      let updTitle = 'Room Updated';
      let updMsg = `${this.editRoomData.name} successfully updated.`;
      if (type === 'deleted') {
        updTitle = 'Room Deleted';
        updMsg = `${this.editRoomData.name} successfully deleted.`;
      }

      this.$notify({ type: 'success', title: updTitle, text: updMsg
      });
    },
    goToProjectOverview() {
      this.$router.push({ name: 'projectOverview', params: { projectId: this.project.id } });
    },
    togglePricing() {
      this.$store.dispatch("system/togglePricing");
    },
    totalProductsCount(product, customProduct, placeholderProduct){
      let total = 0; 
      if(placeholderProduct){
        placeholderProduct.forEach(el => {
          total = total + el.qty;
        });
      } 
      return product + customProduct + total;
    },
    getRoomCost(room) {
      let retailPrice = 0;
      
      // 'Normal' Products
      room.room_products.forEach((el) => {
        retailPrice += el.customer_price;
      });

      // Custom Products
      room.room_custom_products.forEach((el) => {
        retailPrice += el.customer_price;
      });

      // Placeholder Products
      // room.room_placeholder_products.forEach((el) => {
      //   retailPrice += el.placeholder_product.retail_price * el.qty;
      // });

      return retailPrice;
    }
  }
};
</script>